import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Outlet } from 'react-router-dom';
import IndexLink from './partials/IndexLink';
import { browserHistory } from '../helpers/components';
import classnames from 'classnames';
import _get from 'lodash/get';
import _partial from 'lodash/partial';
import _isEmpty from 'lodash/isEmpty';

import {
  setLoginPending,
  clearLoginPending,
  initKeycloak,
  markKcSettled,
  isLogoutPending,
  clearLogoutPending,
  getKeycloakEmail,
  kcDebugSnapshot,
  KEYCLOAK_STATUS
} from '../utils/keycloak';

import { IntlProvider, FormattedMessage } from 'react-intl';
import * as BookingActions from '../actions/booking';
import * as CreditCardActions from '../actions/creditCard';
import * as UserActions from '../actions/user';
import * as PopupActions from '../actions/popup';
import * as i18nActions from '../actions/i18nActions';
import * as BrandActions from '../actions/brand';
import * as KeycloakActions from '../actions/keycloak';
import * as ImpersonateActions from '../actions/impersonate';
import { startLoginOrExplain } from '../actions/login';
import { getDefaultLocale } from '../i18n/locales';

import memberIcon from './../assets/images/member-placeholder.svg';
import { DropdownButton, Dropdown } from 'react-bootstrap';
import SendActivationEmailForm from './forms/SendActivationEmailForm';

import {
  formatDataForSearchBookingsApiCall,
  setMomentTimeForRequest,
  getObjectFromHash,
  getObjectFromQuery
} from '../helpers';

import Popup from './popup/Popup';
import BookingRecapPopup from './popup/BookingRecapPopup';
import CreditCardPopup from './popup/CreditCardPopup';
import BookingCancelPopup from './popup/BookingCancelPopup';
import IntlGlobalProvider from '../utils/IntlGlobalProvider';

import {
  gtmPushBasicSearchData,
  getMessageOptions,
  isDevEnv,
  fallbackFunc,
  getAppObj,
  safe,
  isHomePage,
  isBookingPage,
  getNavigatorLanguage,
  getMessage as t,
  navigateToCreationPage,
  sessionStorageSetJson,
  isRegistrationFlowPage
} from '../utils/utils';

import SearchBookingsHomepageForm from './forms/SearchBookingsHomepageForm';
import GenericPopup from './popup/GenericPopup';
import BookingSuccessPopup from './popup/BookingSuccessPopup';
import Footer from './partials/Footer';
import AppSpinner from './partials/AppSpinner';
import FaqPopup from './popup/FaqPopup';
import { getFaqLink } from '../helpers/components';

import {
  searchValuesSelector,
  userCompanyIdSelector,
  keycloakConfigSelector,
  isIncompleteUserSelector,
  userIdSelector,
  currentThemeSelector,
  isPrimaryColorDarkSelector,
  isSecondaryColorDarkSelector,
  showAppLoaderSelector,
  brandIdSelector,
  keycloakRegistrationFlagsSelector,
  profileCompanyIdsSelector,
  messagesSelector,
  isAppliedUserSelector,
  isExternalIncompleteUserSelector
} from '../helpers/selectors';

import {
  FALLBACK_DECISION,
  filterUnlinkedCompanyItems,
  getPublicCompanyFallbackDecision,
  normalizePublicCompaniesResponse
} from '../helpers/ssoRegistrationFlow';

import HowItWorks from './HowItWorks';
import { pt_BR_map } from '../actions/i18nActions/helpers';
import { keysMemberStatus } from '../constants/translations';
import BookingEditPopup from './popup/BookingEditPopup';
import config from '../constants/config';

import Flash from '../utils/flashMessage/selector';
import FlashMessage from '../utils/flashMessage/lib';
import { LEGACY_SUBSCRIBE, STORAGE_KEY } from '../constants/generic';
import PaymentErrorPopup from './popup/PaymentErrorPopup';
import { getErrorStatus } from '../helpers/formatters';
import { addActionError, addErrorMsg, addInfoMsg } from '../utils/flashMessage/creator';
import memoizeOne from 'memoize-one';
import DeepLinkMessage from './popup/DeepUrlMessage';
import AnonymizeConfirmPopup from './popup/AnonymizeConfirmPopup';
import ConfirmPopup from './popup/ConfirmPopup';
import MobileAppSelectorPopup from './popup/MobileAppSelectorPopup';
import VrInfoPopup from './popup/VrInfoPopup';
import { getErrorMsg } from '../helpers/errors';
import { book, validateAccount } from '../constants/routes';
import {
  clearAuthTokens,
  getAppAuthTokens,
  getAppSsoTokens,
  restoreAuthTokens,
  restoreSsoTokens,
  saveSsoTokens
} from '../utils/tokens';

// [KC-DEBUG] Runs once, at module evaluation - the very first line of the flow.
console.log('[KC-DEBUG] App.js/module-init: BOOT >>> env=' + JSON.stringify(getAppObj().settings?.env) + ' origin=' + window.location.origin + ' configEnv=' + JSON.stringify(config.env) + ' <<<'); // eslint-disable-line no-console
console.log('[KC-DEBUG] App.js/module-init: BOOT - environment identity (logged exactly once per page load)', { appSettingsEnv: getAppObj().settings?.env, appSettingsEnvType: typeof getAppObj().settings?.env, appSettingsEnvJson: JSON.stringify(getAppObj().settings?.env), appSettingsKeys: getAppObj().settings ? Object.keys(getAppObj().settings).join(',') : null, configEnv: config.env, origin: window.location.origin, hostname: window.location.hostname, protocol: window.location.protocol, pathname: window.location.pathname, isDevEnv: isDevEnv(), navigatorCookieEnabled: navigator.cookieEnabled, userAgent: navigator.userAgent, atIso: new Date().toISOString(), perfNowMs: Math.round(performance.now()) }); // eslint-disable-line no-console
console.log('[KC-DEBUG] App.js/module-init: BOOT - state snapshot before any app code runs', kcDebugSnapshot()); // eslint-disable-line no-console

// [KC-DEBUG] call counters so repeated React update cycles stay tellable apart.
let kcDebugHandleTokensCalls = 0;
let kcDebugHandleKeycloakCalls = 0;
let kcDebugRenderCalls = 0;
let kcDebugLastRenderSignature = '';

// Where each company-fallback decision lands. Three decisions share /select-company:
// the screen renders the picker, the invite-only body or the "registration
// unavailable" message itself, from the same store data the decision was made on.
const FALLBACK_HANDLERS = {
  [FALLBACK_DECISION.FLAGS]: () => browserHistory.push('/sso-register-company'),
  [FALLBACK_DECISION.SINGLE_COMPANY]: ({ companyId }) =>
    navigateToCreationPage(companyId),
  [FALLBACK_DECISION.COMPANY_PICKER]: () => browserHistory.push('/select-company'),
  [FALLBACK_DECISION.INVITE_ONLY]: () => browserHistory.push('/select-company'),
  [FALLBACK_DECISION.UNAVAILABLE]: () => browserHistory.push('/select-company')
};

// The routes the fallback cascade lands on. Already being there means the screen
// owns the job: SelectBrandCompany mounts its own fetch-and-decide, so a second
// cascade from App would double-fetch and race the screen's navigation.
const FALLBACK_TARGET_PATHNAMES = ['/sso-register-company', '/select-company'];

const footerHideLocations = [`/${book}`];

const hashRoutes = {
  [validateAccount]: true
};

function getSizeProps() {
  return {
    isMobile: window.innerWidth < 768,
    isTablet: window.innerWidth < 1550
  };
}

function loadGtmScript(gtmId, onLoad) {
  const [w, d, s, l] = [window, document, 'script', 'dataLayer'];

  w[l] = w[l] || [];
  w[l].push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  });

  // noinspection EqualityComparisonWithCoercionJS
  const f = d.getElementsByTagName(s)[0],
    j = d.createElement(s),
    // eslint-disable-next-line eqeqeq
    dl = l != 'dataLayer' ? '&l=' + l : '';

  j.async = true;
  j.src = 'https://www.googletagmanager.com/gtm.js?id=' + gtmId + dl;
  j.onload = onLoad;
  f.parentNode.insertBefore(j, f);
}

function addTagManagerToDom(gtmId) {
  loadGtmScript(gtmId, () => {
    getAppObj().gtmId = gtmId;
  });
}

function setFlashMessage() {
  getAppObj().Flash = Flash;
  getAppObj().FlashMessage = FlashMessage;
}

function getCssThemeConfig(brandThemeObj, important = false) {
  let css = '';

  if (_isEmpty(brandThemeObj)) return css;

  important = important ? ' !important' : '';

  for (let [key, value] of Object.entries(brandThemeObj)) {
    css += key + ':' + value + important + ';';
  }

  return css;
}

function getCssProps(btnThemed, defaultProps) {
  if (btnThemed === '') return 'background-color:' + defaultProps + ';';
  return btnThemed;
}

export class App extends Component {
  constructor(props) {
    super(props);

    this.state = {
      mobileMenu: false,
      isMobile: false,
      isTablet: false
    };

    const kcDebugCtorT0 = performance.now();

    console.log('[KC-DEBUG] App.js/constructor: ENTER - nothing Keycloak-related has run yet', { atIso: new Date().toISOString(), perfNowMs: Math.round(kcDebugCtorT0), configFetched: props.brand?.configFetched, configEnv: config.env, appSettingsEnv: getAppObj().settings?.env, apiUrlNow: config.getApiUrl(), hasUserData: Boolean(props.user?.data) }); // eslint-disable-line no-console

    restoreAuthTokens();
    restoreSsoTokens();
    setFlashMessage();

    this.setLocale();
    this.setCallbacks(props);

    console.log('[KC-DEBUG] App.js/constructor: WAITING on config.getApiUrlAsync(3000, 100) BEFORE getAwsBrand - the whole KC flow is queued behind this promise', { apiUrlBefore: config.getApiUrl(), appApiUrl: getAppObj().apiUrl, sessionApiUrl: safe(() => window.sessionStorage.getItem('API_URL')), timeoutMs: 3000, intervalMs: 100 }); // eslint-disable-line no-console
    // Wait for runtime API URL to be available before making remote calls
    // (ensures window.APP.apiUrl or sessionStorage.API_URL is populated on some deployments)
    config
      .getApiUrlAsync(3000, 100)
      .then((apiUrl) => {
        console.log('[KC-DEBUG] App.js/constructor: getApiUrlAsync RESOLVED -> calling getAwsBrand + componentPropsUpdated', { apiUrl, waitedMs: Math.round(performance.now() - kcDebugCtorT0), resolvedToBackupUrl: String(apiUrl).indexOf('gwweb-prod-glide') !== -1, configEnv: config.env }); // eslint-disable-line no-console
        this.getRemoteData(props);
        this.componentPropsUpdated(props, true);
      })
      .catch((e) => {
        console.log('[KC-DEBUG] App.js/constructor: getApiUrlAsync REJECTED -> fallback path, still calling getAwsBrand', { waitedMs: Math.round(performance.now() - kcDebugCtorT0), error: String(e), errorMessage: e?.message }); // eslint-disable-line no-console
        // fallback: still attempt remote init
        this.getRemoteData(props);
        this.componentPropsUpdated(props, true);
      });
  }

  componentDidMount() {
    this.addUnloadListener();
    this.addPageShowListener();
    this.addResizeListener();
    this.setLegacyTokenUpdates();
    this.onResize();
    console.log("---> DEBUG: APP MOUNTED");
  }

  componentWillUnmount() {
    this.removeResizeListener();
  }

  addUnloadListener() {
    window.addEventListener(
      'beforeunload',
      () => {
        const { user } = this.props;
        const { data: userData } = user;

        // Une deconnexion Keycloak est en cours : ne pas re-persister l'utilisateur
        // (sinon la session serait restauree au reload et l'utilisateur resterait
        // connecte). On purge au contraire la session persistee.
        if (isLogoutPending()) {
          sessionStorage.removeItem(STORAGE_KEY.USER);
          return;
        }

        if (userData?.id) {
          sessionStorageSetJson(STORAGE_KEY.USER, userData);
        }
      },
      { capture: true }
    );
  }

  addPageShowListener() {
    window.addEventListener('pageshow', (event) => {
      // Retour depuis le bfcache (ex: bouton "retour" du navigateur depuis la page
      // Keycloak sans avoir termine la connexion). Le JS n'est pas re-execute, donc
      // le loader affiche juste avant la redirection resterait fige indefiniment.
      // On le masque et on nettoie l'etat de connexion en attente.
      console.log('[KC-DEBUG] App.js/pageshow: page shown - DECISION `if (event.persisted)` (true => came back from bfcache, e.g. browser Back from the KC login page; loader forced off)', { persisted: event.persisted, atIso: new Date().toISOString(), showAppLoader: this.props.brand?.showAppLoader, ...kcDebugSnapshot() }); // eslint-disable-line no-console
      if (event.persisted) {
        clearLoginPending();
        this.props.setAppLoaderState(false);
      }
    });
  }

  addResizeListener() {
    window.addEventListener('resize', this.onResize, { capture: true });
  }

  removeResizeListener() {
    window.removeEventListener('resize', this.onResize, { capture: true });
  }

  setLegacyTokenUpdates() {
    setInterval(() => {
      const authTokens = getAppAuthTokens();

      if (authTokens.refreshToken) {
        this.props.updateTokenSilent(authTokens.refreshToken).catch(fallbackFunc);
      }
    }, 3600000);
  }

  showErrorMsg(data) {
    addErrorMsg(
      getErrorMsg({
        bundle: this.props.m,
        error: ((data.error || {}).response || {}).data
      })
    );
  }

  setCallbacks(props) {
    this.hideGenericPopupAction = () => props.popup_Generic_SetVisibility(false);

    this.closeRecapPopup = () => {
      this.props.popup_BookingRecap_SetVisibility(false);
      this.props.emptyVehicleData();
    };

    this.closeBookingCancelPopup = () => {
      this.props.popup_BookingCancel_SetVisibility(false);
      this.props.emptyBookingCancelId();
    };

    this.closeBookingSuccessPopup = () => {
      this.props.popup_BookingSuccess_SetVisibility(false);
      this.props.clearAddToCalendarData();
      this.props.setRefreshBookingsFlag();
    };

    this.closeCreditCardPopup = () => {
      const { popup_CreditCard_SetVisibility } = this.props;

      popup_CreditCard_SetVisibility(false);
    };

    this.closeAnonymizeConfirmPopup = () => {
      const { popup_AnonymConfirm_SetVisibility } = this.props;

      popup_AnonymConfirm_SetVisibility(false);
    };

    this.handleAnonymizeConfirmPopup = () => {
      const { selfAnonymize } = this.props;

      this.closeAnonymizeConfirmPopup();
      selfAnonymize().then((data) => {
        if (data?.type === 'user/SELF_ANONYMIZE_SUCCESS') {
          addInfoMsg(t(this.props.m, 'registered.profile.anonymize.success'));
        } else {
          this.showErrorMsg(data);
        }
      });
    };

    this.openRegistrationCancelPopup = () =>
      this.props.popup_RegistrationCancel_SetVisibility(true);

    this.closeRegistrationCancelPopup = () =>
      this.props.popup_RegistrationCancel_SetVisibility(false);

    this.handleConfirmCancelRegistration = () => {
      this.props.popup_RegistrationCancel_SetVisibility(false);
      this.props.handleKeycloakLogout();
    };

    this.handleGetCreditCard = () => {
      const { getCreditCardURL, userId, m } = this.props;

      if (userId) getCreditCardURL();
      else addErrorMsg(t(m, 'error.session.expired'));

      this.closeCreditCardPopup();
    };

    this.closeFaqPopup = () => {
      this.props.popup_Faq_SetVisibility(false);
    };

    this.closeMobileAppSelectorPopup = () => {
      this.props.popup_MobileAppSelector_SetVisibility(false);
    };

    this.closeVrInfoPopup = () => {
      this.props.popup_VrInfo_SetVisibility(false);
    };

    this.closeBookingEditPopup = () => {
      this.props.popup_BookingEdit_SetVisibility(false);
      this.props.clearBookingEditPopupData();
    };

    this.toggleMobileMenu = () => {
      this.setState({
        mobileMenu: !this.state.mobileMenu
      });
    };

    this.closeMobileMenu = () => {
      this.setState({
        mobileMenu: false
      });
    };

    this.openFaqPopup = () => {
      this.props.popup_Faq_SetVisibility(true);
      this.closeMobileMenu();
    };

    this.searchBookings = (data) => {
      const {
        search,
        brand: { branded },
        userId
      } = this.props;

      const companyId = _get(this.props.user, 'data.company.id');
      const searchFormValues = data;

      const formattedData = formatDataForSearchBookingsApiCall(
        setMomentTimeForRequest(data),
        companyId,
        branded.contract
      );

      search(formattedData).then((data) => {
        if (data.type === 'booking/SEARCH_SUCCESS') {
          gtmPushBasicSearchData(data, searchFormValues, userId);
        }
      });
    };

    this.handleSendActivationEmail = (data) => {
      const { login } = data || {};

      if (login) this.props.sendActivationEmail(login);
    };

    this.closeTabletBurger = () => {
      if (window.innerWidth >= 1550) {
        if (document.getElementById('menu-btn')) {
          document.getElementById('menu-btn').checked = false;
        }
      }
    };

    this.onResize = () => {
      this.setState(getSizeProps());
      this.closeTabletBurger();
    };

    const handleHashRoutesMem = memoizeOne((hash) => {
      if (hash) {
        const split = hash.split('/');
        const route = split[1];

        if (hashRoutes[route]) {
          browserHistory.replace(hash.substring(1));
        }
      }
    });

    this.handleHashRoutes = () => {
      handleHashRoutesMem(this.props.location.hash);
    };

    const handleSubscribeRedirectMem = memoizeOne((hash) => {
      if (hash) {
        const split = hash.split('/');
        const page = split[1];
        const id = split[2];

        if (page === LEGACY_SUBSCRIBE && id) {
          navigateToCreationPage(id);
        }
      }
    });

    this.handleSubscribeRedirect = () => {
      handleSubscribeRedirectMem(this.props.location.hash);
    };

    const canShowFooterMem = memoizeOne((pathname) => {
      return !footerHideLocations.includes(pathname);
    });

    this.canShowFooter = () => canShowFooterMem(this.props.location.pathname);

    const getPageClassMem = memoizeOne((pathname) => {
      return pathname.replaceAll('/', '_');
    });

    this.getPageClass = () => getPageClassMem(this.props.location.pathname);

    // Straight to Keycloak when it is usable, an explicit "login unavailable"
    // popup otherwise - the shared thunk owns that fork.
    this.handleLoginClick = () => this.props.startLoginOrExplain();

    this.popupQueryCallbacks = {
      'account-activation-popup': () =>
        this.props.popup_AccountValidation_SetVisibility(true),
      'activation-email-sent-popup': () =>
        this.props.popup_ActivationEmailSent_SetVisibility(true)
    };
  }

  handleIncompleteUser() {
    const p = this.props;

    console.log('[KC-DEBUG] App.js/handleIncompleteUser: operands of the incomplete-user redirect decision', { isIncompleteUser: p.isIncompleteUser, isAppliedUser: p.isAppliedUser, incompleteChecked: Boolean(getAppObj().incompleteChecked), isExternalIncompleteUser: p.isExternalIncompleteUser, willRedirect: Boolean((p.isIncompleteUser || p.isAppliedUser) && !getAppObj().incompleteChecked) }); // eslint-disable-line no-console
    if ((p.isIncompleteUser || p.isAppliedUser) && !getAppObj().incompleteChecked) {
      getAppObj().incompleteChecked = true;
      p.handleIncompleteUserRedirect();
    }
  }

  verifyExternalLogin() {
    const kcDebugT0 = performance.now();

    console.log('[KC-DEBUG] App.js/verifyExternalLogin: ENTER #timestamped - reaching this line means kc.init() RESOLVED with authenticated=true', { atIso: new Date().toISOString(), perfNowMs: Math.round(kcDebugT0), ...kcDebugSnapshot() }); // eslint-disable-line no-console
    console.log('[KC-DEBUG] App.js/verifyExternalLogin: ENTER - saving SSO tokens then calling GET /v2/members/me', { hasKcToken: Boolean(getAppObj().keycloak?.token), kcTokenLength: getAppObj().keycloak?.token?.length }); // eslint-disable-line no-console
    saveSsoTokens();

    console.log('[KC-DEBUG] App.js/verifyExternalLogin: AFTER saveSsoTokens - about to dispatch verifyUser()', { hasSsoTokenInAppObj: Boolean(getAppSsoTokens().token), ssoTokenLength: getAppSsoTokens().token?.length, hasAuthToken: Boolean(getAppAuthTokens().token), apiBaseUrl: config.getApiUrl() }); // eslint-disable-line no-console
    this.props.verifyUser().then((resp) => {
      console.log('[KC-DEBUG] App.js/verifyExternalLogin: /me RESPONDED after ' + Math.round(performance.now() - kcDebugT0) + 'ms', { tookMs: Math.round(performance.now() - kcDebugT0), atIso: new Date().toISOString(), respType: resp?.type }); // eslint-disable-line no-console
      console.log('[KC-DEBUG] App.js/verifyExternalLogin: /me RESPONDED - DECISION `resp.type === VERIFY_USER_SUCCESS`', { respType: resp?.type, isSuccess: resp?.type === 'keycloak/VERIFY_USER_SUCCESS', httpStatus: getErrorStatus(resp), hasUserId: Boolean(resp?.payload?.data?.id), memberStatus: resp?.payload?.data?.status }); // eslint-disable-line no-console
      this.ssoHideLoader(true, 'verifyExternalLogin:/me-responded');

      if (resp.type === 'keycloak/VERIFY_USER_SUCCESS') {
        this.props.handleLoginSuccess(resp);
      } else {
        this.props.handleVerifyUserFail(resp);

        // Keycloak-authenticated user with no member yet (GET /me -> 404): resolve a
        // company then continue into registration - mirror of the mobile flow.
        // Non-404 failures already surfaced their specific message above.
        console.log('[KC-DEBUG] App.js/verifyExternalLogin: /me FAILED - DECISION `getErrorStatus(resp) === 404` (404 => SSO registration flow; other => error message only, user stays logged out)', { httpStatus: getErrorStatus(resp), is404: getErrorStatus(resp) === 404 }); // eslint-disable-line no-console
        if (getErrorStatus(resp) === 404) {
          this.resolveSsoRegistration();
        }
      }
    });
  }

  // KC-authenticated user with no member yet. Resolve which company to register into,
  // then land straight on the creation form - mirror of the mobile flow: ask the
  // backend by email (GET /v2/companies/sso-registration?email). Only when that
  // resolves nothing do we fall back to the manual company pickers
  // (navigateToCompanyFallback).
  resolveSsoRegistration() {
    const email = getKeycloakEmail();

    console.log('[KC-DEBUG] App.js/resolveSsoRegistration: DECISION `if (!email)` (no email => company fallback pickers)', { hasEmail: Boolean(email), emailDomain: email?.split('@')[1], willUseFallback: !email }); // eslint-disable-line no-console
    if (!email) {
      this.navigateToCompanyFallback();
      return;
    }

    this.props
      .getRemoteKeycloakConfig(email)
      .then((resp) => {
        const resolvedId =
          resp.type === 'keycloak/GET_REMOTE_CONFIG_SUCCESS' &&
          resp.payload.data &&
          resp.payload.data.superCompanyId;

        console.log('[KC-DEBUG] App.js/resolveSsoRegistration: /v2/companies/sso-registration RESPONDED - DECISION `if (resolvedId)`', { respType: resp?.type, resolvedId, hasResolvedId: Boolean(resolvedId) }); // eslint-disable-line no-console
        if (resolvedId) {
          navigateToCreationPage(resolvedId);
        } else {
          this.navigateToCompanyFallback();
        }
      })
      .catch((e) => {
        console.log('[KC-DEBUG] App.js/resolveSsoRegistration: /v2/companies/sso-registration THREW => company fallback', { error: String(e) }); // eslint-disable-line no-console
        return this.navigateToCompanyFallback();
      });
  }

  // Manual company resolution, driven by the S3 keycloak block instead of brand
  // names (mirror of the mobile brand fork): a brand that publishes its countries
  // in the config opts into the flags screen with useCompanyFlags; everyone else
  // gets the mobile cascade over GET /v2/brands/{id}/super-companies/public.
  navigateToCompanyFallback() {
    const { useCompanyFlags, allowRegisterWithInvitationCode } =
      this.props.registrationFlags;
    const { pathname } = this.props.location;

    // F5 / deep link straight onto a chooser screen: leave the work to the screen.
    if (FALLBACK_TARGET_PATHNAMES.includes(pathname)) {
      console.log('[KC-DEBUG] App.js/navigateToCompanyFallback: SKIPPED - already on a fallback target route, the screen owns the fetch/decision', { pathname }); // eslint-disable-line no-console
      return;
    }

    console.log('[KC-DEBUG] App.js/navigateToCompanyFallback: DECISION `if (useCompanyFlags)` (true => flags screen, no API call; false => cascade over super-companies/public)', { useCompanyFlags, allowRegisterWithInvitationCode }); // eslint-disable-line no-console
    if (useCompanyFlags) {
      FALLBACK_HANDLERS[FALLBACK_DECISION.FLAGS]();
      return;
    }

    this.props.getBrandCompanies(this.props.brandId).then((resp) => {
      // A failed request has already flashed its error, and it is not the same
      // thing as a brand with zero companies - nothing to navigate on.
      if (resp.type !== 'user/GET_BRAND_COMPANIES_SUCCESS') return;

      const companies = normalizePublicCompaniesResponse(resp.payload.data);

      if (!companies) return;

      const availableCompanies = filterUnlinkedCompanyItems(
        companies,
        this.props.profileCompanyIds,
        (company) => company.id
      );
      const decision = getPublicCompanyFallbackDecision({
        availableCompanies,
        allowInvite: allowRegisterWithInvitationCode
      });

      console.log('[KC-DEBUG] App.js/navigateToCompanyFallback: cascade DECISION over super-companies/public', { total: companies.length, available: availableCompanies.length, allowRegisterWithInvitationCode, decision }); // eslint-disable-line no-console
      FALLBACK_HANDLERS[decision]({ companyId: availableCompanies[0]?.id });
    });
  }

  handleKeycloak(props) {
    const kcDebugT0 = performance.now();

    kcDebugHandleKeycloakCalls += 1;

    console.log('[KC-DEBUG] App.js/handleKeycloak: ENTER #' + kcDebugHandleKeycloakCalls + ' - timestamped entry', { call: kcDebugHandleKeycloakCalls, atIso: new Date().toISOString(), perfNowMs: Math.round(kcDebugT0), keycloakInitFlag: getAppObj().keycloakInit }); // eslint-disable-line no-console
    console.log('[KC-DEBUG] App.js/handleKeycloak: ENTER - `if (getAppObj().keycloakInit) return` guard', { alreadyInit: Boolean(getAppObj().keycloakInit), willBailAsAlreadyInit: Boolean(getAppObj().keycloakInit) }); // eslint-disable-line no-console
    if (getAppObj().keycloakInit) {
      console.log('[KC-DEBUG] App.js/handleKeycloak: EARLY EXIT 1 - keycloakInit is already true, returning immediately (a previous cycle owns the init)', { keycloakInitFlag: getAppObj().keycloakInit, keycloakInitFlagType: typeof getAppObj().keycloakInit, call: kcDebugHandleKeycloakCalls, hasKcClient: Boolean(getAppObj().keycloak) }); // eslint-disable-line no-console
      return;
    }
    getAppObj().keycloakInit = true;

    const { keycloakConfig } = props;
    const { initSettings } = keycloakConfig || {};

    console.log('[KC-DEBUG] App.js/handleKeycloak: DECISION - `if (!initSettings) return this.ssoDelayLoader()` (bail here => KC never inits => /me NEVER called)', { hasKeycloakConfig: Boolean(keycloakConfig), keycloakConfigKeys: keycloakConfig ? Object.keys(keycloakConfig) : null, hasInitSettings: Boolean(initSettings), initSettings, identityProvider: keycloakConfig?.identityProvider, useBrandConfig: keycloakConfig?.useBrandConfig, willBail: !initSettings }); // eslint-disable-line no-console
    console.log('[KC-DEBUG] App.js/handleKeycloak: `!initSettings` guard operands flattened (keycloakConfig comes straight out of selectors.js/parseKeycloakConfig)', { keycloakConfigType: typeof keycloakConfig, keycloakConfigKeysCsv: keycloakConfig ? Object.keys(keycloakConfig).join(',') : null, initSettingsType: typeof initSettings, initSettingsJson: safe(() => JSON.stringify(initSettings)), initClientId: initSettings?.clientId, initRealm: initSettings?.realm, initUrl: initSettings?.url, isDegradedButtonTextOnly: Boolean(keycloakConfig && Object.keys(keycloakConfig).length === 1 && keycloakConfig.buttonText) }); // eslint-disable-line no-console
    if (!initSettings) {
      console.log('[KC-DEBUG] App.js/handleKeycloak: EARLY EXIT 2 - no initSettings => initKeycloak is NEVER called => /me NEVER called; the loader is just dismissed', { call: kcDebugHandleKeycloakCalls }); // eslint-disable-line no-console
      // This brand has no Keycloak, so kc.init() will never run and never settle.
      // Release the request interceptor's SSO wait so a KC-less brand cannot deadlock.
      markKcSettled();
      return this.ssoDelayLoader('handleKeycloak:!initSettings');
    }

    console.log('[KC-DEBUG] App.js/handleKeycloak: CALLING initKeycloak now - from here on watch keycloak.js/initKeycloak lines', { atIso: new Date().toISOString(), sinceHandleKeycloakEnterMs: Math.round(performance.now() - kcDebugT0), initClientId: initSettings.clientId, initRealm: initSettings.realm, initUrl: initSettings.url }); // eslint-disable-line no-console
    initKeycloak(initSettings, (status, error) => {
      const kcDebugCbAfterMs = Math.round(performance.now() - kcDebugT0);

      // Publish the outcome before acting on it: this is the only place that knows
      // whether Keycloak answered at all, and FAIL is not UNAUTH. Everything below
      // funnels both into ssoDelayLoader, which merely hides the loader on a timeout.
      this.props.setKeycloakInitStatus(status);

      // Status name and error message inlined into the STRING on purpose - object
      // payloads can collapse to a bare "Object" depending on how the console is read.
      console.log('[KC-DEBUG] App.js/handleKeycloak: initKeycloak CALLBACK >>> status=' + { 0: 'AUTH', 1: 'UNAUTH', 2: 'FAIL' }[status] + ' after ' + Math.round(performance.now() - kcDebugT0) + 'ms <<< error=' + String(error?.message)); // eslint-disable-line no-console
      console.log('[KC-DEBUG] App.js/handleKeycloak: initKeycloak callback `error` argument (populated ONLY on FAIL; the app currently drops it on the floor)', { hasError: Boolean(error), errorMessage: error?.message, errorName: error?.name, errorString: error === undefined ? undefined : String(error), errorJson: safe(() => JSON.stringify(error)), is3pCookieTimeout: String(error?.message || '').indexOf('3rd party check iframe') !== -1 }); // eslint-disable-line no-console

      console.log('[KC-DEBUG] App.js/handleKeycloak: initKeycloak CALLBACK fired after ' + kcDebugCbAfterMs + 'ms from handleKeycloak ENTER (~10000ms + FAIL == the 3p-cookie timeout)', { status, statusName: { 0: 'AUTH', 1: 'UNAUTH', 2: 'FAIL' }[status], msSinceHandleKeycloakEnter: kcDebugCbAfterMs, atIso: new Date().toISOString(), ...kcDebugSnapshot() }); // eslint-disable-line no-console
      console.log('[KC-DEBUG] App.js/handleKeycloak: initKeycloak callback - DECISION `status === AUTH`', { status, statusName: { 0: 'AUTH', 1: 'UNAUTH', 2: 'FAIL' }[status], isAuth: status === KEYCLOAK_STATUS.AUTH, logoutPending: Boolean(isLogoutPending()), loginPending: Boolean(localStorage.getItem(STORAGE_KEY.LOGIN_PENDING)) }); // eslint-disable-line no-console
      if (status === KEYCLOAK_STATUS.AUTH) {
        if (isLogoutPending()) {
          // Retour d'un logout explicite : le check-sso peut encore reporter une
          // session active (iframe login-status pas encore rafraichie). On ne
          // reconnecte pas l'utilisateur, on finalise juste la deconnexion.
          console.log('[KC-DEBUG] App.js/handleKeycloak: AUTH but logoutPending => finalising logout, NOT logging in (/me NOT called)'); // eslint-disable-line no-console
          clearLogoutPending();
          this.ssoDelayLoader('handleKeycloak:AUTH+logoutPending');
          return;
        }
        console.log('[KC-DEBUG] App.js/handleKeycloak: AUTH + no logoutPending => calling verifyExternalLogin (this is the ONLY path to /me)'); // eslint-disable-line no-console
        this.verifyExternalLogin();
      } else {
        console.log('[KC-DEBUG] App.js/handleKeycloak: status NOT AUTH => no KC session to restore, /me NOT called', { status, statusName: { 0: 'AUTH', 1: 'UNAUTH', 2: 'FAIL' }[status] }); // eslint-disable-line no-console
        console.log('[KC-DEBUG] App.js/handleKeycloak: THIS IS THE SILENT DEAD END - UNAUTH/FAIL both draw a local guest, but FAIL never calls the Keycloak logout endpoint', { status, statusName: { 0: 'AUTH', 1: 'UNAUTH', 2: 'FAIL' }[status], isFail: status === KEYCLOAK_STATUS.FAIL, isUnauth: status === KEYCLOAK_STATUS.UNAUTH, msSinceHandleKeycloakEnter: kcDebugCbAfterMs }); // eslint-disable-line no-console
        // Aucune session Keycloak active : rien a restaurer (les tokens SSO ne sont
        // jamais persistes). La reconnexion se fait uniquement via le check-sso.
        this.props.clearCachedUserForCurrentAuthState();
        clearLogoutPending();
        this.ssoDelayLoader('handleKeycloak:NOT_AUTH:' + status);
      }
    });
  }

  ssoHideLoader(clear = true, from = 'unknown') {
    console.log('[KC-DEBUG] App.js/ssoHideLoader: HIDING the app loader now', { from, clearLoginPending: clear, atIso: new Date().toISOString(), perfNowMs: Math.round(performance.now()), showAppLoaderBefore: this.props.brand?.showAppLoader, hasUserData: Boolean(this.props.user?.data), userId: this.props.userId }); // eslint-disable-line no-console
    this.props.setAppLoaderState(false);
    if (clear) clearLoginPending();
  }

  setLoginStatus(status) {
    this.weDidLogin = status;
  }

  weCanLogin() {
    return !this.weDidLogin;
  }

  handleOurTokens(props) {
    const { impersonateToken } = getObjectFromHash() || {};

    console.log('[KC-DEBUG] App.js/handleOurTokens: DECISION - impersonate token from hash vs legacy in-memory token restore (runs just BEFORE handleKeycloak)', { hasImpersonateToken: Boolean(impersonateToken), impersonateTokenLength: impersonateToken?.length, weCanLogin: this.weCanLogin(), weDidLogin: Boolean(this.weDidLogin), hashLength: window.location.hash?.length, hasUserData: Boolean(props.user?.data) }); // eslint-disable-line no-console
    if (impersonateToken) {
      this.handleImpersonateToken(impersonateToken);
      this.setLoginStatus(true);
    } else if (this.weCanLogin()) {
      this.handleLoginUser(props);
      this.setLoginStatus(true);
    }
  }

  handleTokens() {
    const props = this.props;

    kcDebugHandleTokensCalls += 1;

    console.log('[KC-DEBUG] App.js/handleTokens: ENTER #' + kcDebugHandleTokensCalls + ' - timestamped entry (componentPropsUpdated calls this on EVERY React update cycle)', { call: kcDebugHandleTokensCalls, atIso: new Date().toISOString(), perfNowMs: Math.round(performance.now()), configFetched: props.brand.configFetched, keycloakInitDone: Boolean(getAppObj().keycloakInit), hasUserData: Boolean(props.user?.data), showAppLoader: props.brand.showAppLoader }); // eslint-disable-line no-console
    console.log('[KC-DEBUG] App.js/handleTokens: DECISION `if (props.brand.configFetched)` (false => handleKeycloak NEVER runs => /me never called)', { configFetched: props.brand.configFetched, brandId: props.brand.branded?.brandId, hostname: window.location.hostname, hasBrandKeycloakBlock: Boolean(props.brand.branded?.keycloak), keycloakInitDone: Boolean(getAppObj().keycloakInit) }); // eslint-disable-line no-console
    if (props.brand.configFetched) {
      this.handleOurTokens(props);
      this.handleKeycloak(props);
    } else {
      console.log('[KC-DEBUG] App.js/handleTokens: BAILED on this cycle - configFetched is falsy, handleKeycloak NOT reached', { configFetched: props.brand.configFetched, configFetchedType: typeof props.brand.configFetched, call: kcDebugHandleTokensCalls }); // eslint-disable-line no-console
    }
  }

  handleImpersonateToken(impersonateToken) {
    if (!this.fetchingToken) {
      this.fetchingToken = true;
      setLoginPending();
      this.props
        .loginWithRefreshToken(impersonateToken, { useLegacyRefresh: true })
        .then((data) => {
          if (data.type === 'user/REFRESH_SUCCESS') {
            this.props.setImpersonateUserStatus(true);
            this.props.handleLoginSuccess(data);
          } else {
            addActionError(data);
            this.props.clearCachedUserForCurrentAuthState();
          }

          clearLoginPending();
          this.fetchingToken = false;
          browserHistory.push('/');
        });
    }
  }

  restoreSession({ token, refreshToken }) {
    console.log('[KC-DEBUG] App.js/restoreSession: DECISION - legacy (non-KC) session restore from in-memory tokens; all-falsy => returns null and nothing happens', { hasRefreshToken: Boolean(refreshToken), refreshTokenLength: refreshToken?.length, hasToken: Boolean(token), tokenLength: token?.length, path: refreshToken ? 'loginWithRefreshToken' : token ? 'loginWithAuthToken' : 'null (no-op)' }); // eslint-disable-line no-console
    if (refreshToken) return this.props.loginWithRefreshToken(refreshToken);
    if (token) return this.props.loginWithAuthToken(token);
    return null;
  }

  handleLoginUser(props) {
    console.log('[KC-DEBUG] App.js/handleLoginUser: DECISION `if (props.user.data) return` (legacy path only - SSO tokens are never persisted, so this normally no-ops)', { hasUserData: Boolean(props.user.data), userId: props.user.data?.id, willBail: Boolean(props.user.data) }); // eslint-disable-line no-console
    if (props.user.data) return;

    const sessionRestore = this.restoreSession(getAppAuthTokens());

    if (!sessionRestore) return;

    sessionRestore.then((data) => {
      if (data?.type === 'user/REFRESH_SUCCESS') {
        this.props.handleLoginSuccess(data);
      } else {
        clearAuthTokens();
        this.props.clearCachedUserForCurrentAuthState();
      }
    });
  }

  getRemoteData(props) {
    console.log('[KC-DEBUG] App.js/getRemoteData: dispatching getAwsBrand() - the brand config it loads decides whether Keycloak runs at all', { atIso: new Date().toISOString(), perfNowMs: Math.round(performance.now()), hostname: window.location.hostname, configEnv: config.env }); // eslint-disable-line no-console
    props.getAwsBrand();
  }

  setLocale() {
    this.props.setLanguage(getDefaultLocale());
  }

  renderActivationPopup() {
    if (this.props.user.activationSuccess) {
      return (
        <div>
          <p className="title">
            <FormattedMessage id="label.congratulations" />
            <br />
            <FormattedMessage id="label.account_activation_title" />
          </p>
          <DeepLinkMessage />
          <p className="description">
            <FormattedMessage id="label.account_activation_description" />
          </p>
        </div>
      );
    } else if (this.props.user.alreadyActivated) {
      return (
        <p className="single">
          <FormattedMessage id="label.account.already.active" />
        </p>
      );
    } else if (this.props.user.activationExpired) {
      return (
        <SendActivationEmailForm
          onSubmit={this.handleSendActivationEmail}
          i18n={this.props.i18n}
          fetching={this.props.user.sendEmailFetching}
        />
      );
    } else {
      return (
        <p className="error">
          <FormattedMessage id="label.generic.error" />
        </p>
      );
    }
  }

  renderActivationEmailSentPopup() {
    if (this.props.user.sendEmailSuccess)
      return (
        <p className="single">
          <FormattedMessage id="label.activation.link.sent.success" />
        </p>
      );
    else
      return (
        <p className="error">
          <FormattedMessage id="label.generic.error" />
        </p>
      );
  }

  sendActivationEmailListener() {
    const { sendEmailFetching } = this.props.user || {};

    if (sendEmailFetching) this.activationEmailSent = true;
    if (!sendEmailFetching && this.activationEmailSent) {
      this.activationEmailSent = false;
      this.props.popup_AccountValidation_SetVisibility(false);

      browserHistory.push({
        pathname: '/',
        query: { modal: 'activation-email-sent-popup' }
      });
    }
  }

  locationUpdated(pp, init) {
    const { location: prev } = pp;
    const { location: next } = this.props;

    return init || prev !== next;
  }

  handlePopupQueries(pp, init) {
    if (this.locationUpdated(pp, init)) {
      const { search } = this.props.location || {};
      const { modal } = getObjectFromQuery(search) || {};
      const callback = this.popupQueryCallbacks[modal];

      if (callback) callback();
    }
  }

  componentPropsUpdated(pp, init) {
    this.loadTagManager();
    this.handleHashRoutes();
    this.handleSubscribeRedirect();
    this.sendActivationEmailListener();
    this.handleTokens();
    this.handleIncompleteUser();
    this.handlePopupQueries(pp, init);
    this.handleBrandLanguage(pp, init);
  }

  componentDidUpdate(pp) {
    this.handleMobileModalClasses();
    this.componentPropsUpdated(pp);
  }

  isSearchBookingsLayout() {
    return this.props.location.pathname === '/search-bookings';
  }

  brandChanged(pp, init) {
    const { pathname: nv } = this.props.brand.branded;
    const { pathname: pv } = pp.brand.branded;

    return init || nv !== pv;
  }

  handleBrandLanguage(pp, init) {
    const np = this.props;

    if (np.brand.configFetched) {
      const { language: brandLanguages } = np.brand.branded || {};

      if (this.brandChanged(pp, init)) {
        if (brandLanguages) {
          const { locale } = np.i18n;

          if (!brandLanguages.includes(locale)) {
            const browserLanguage = getNavigatorLanguage();

            if (brandLanguages.includes(browserLanguage)) {
              np.setLanguage(browserLanguage);
            } else {
              np.setLanguage('en');
            }
          }
        }
      }
    }
  }

  handleMobileModalClasses() {
    if (this.state.mobileMenu) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
  }

  loadTagManager() {
    const { branded, configFetched } = this.props.brand;
    const { gtmId } = branded || {};

    if (!this.gtmLoaded && configFetched) {
      this.gtmLoaded = true;

      if (gtmId && !isDevEnv()) {
        addTagManagerToDom(gtmId);
      }
    }
  }

  ssoDelayLoader(from = 'unknown') {
    console.log('[KC-DEBUG] App.js/ssoDelayLoader: CALLED - this is the silent give-up path (loader dismissed after a delay, no error surfaced, user left logged out)', { from, delayMs: config.appLoaderMinTime, atIso: new Date().toISOString(), perfNowMs: Math.round(performance.now()), ...kcDebugSnapshot() }); // eslint-disable-line no-console
    setTimeout(() => this.ssoHideLoader(false, 'ssoDelayLoader<-' + from), config.appLoaderMinTime);
    clearLoginPending();
  }

  renderLogo() {
    const {
      brand: { branded },
      theme
    } = this.props;
    const { logo } = theme || {};

    if (theme) {
      return <img src={logo} alt={branded.productName} />;
    }
  }

  renderLogoBgw() {
    const {
      theme,
      brand: { branded }
    } = this.props;
    const { logoMobile } = theme || {};

    if (theme) {
      return <img className="brand-logo" src={logoMobile} alt={branded.productName} />;
    }
  }

  isHeaderBurger() {
    return footerHideLocations.includes(this.props.location.pathname);
  }

  renderBackgroundProps() {
    const { theme } = this.props;
    const { background } = theme || {};
    const color = 'rgba(0, 0, 0, 0.45)';

    if (theme && isHomePage()) {
      return {
        background: `linear-gradient(${color}, ${color}), url(${background}) no-repeat 30% 40% /cover`
      };
    }
  }

  renderDropdownButton() {
    const { user, theme, handleKeycloakLogout } = this.props;
    const { profile } = theme.icons || {};
    const budy = user.data.firstName + ' ' + user.data.lastName;

    return (
      <div className="container-avatar">
        <DropdownButton title={budy} id="dropdown-myaccount" className="account-menu">
          <Dropdown.Item
            eventKey="1"
            onClick={() => browserHistory.push('/profile/personal-info')}
          >
            <span className="sub-menu">
              <FormattedMessage id="label.my_account" />
            </span>
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item eventKey="2" onClick={handleKeycloakLogout}>
            <span className="sub-menu">
              <FormattedMessage id="label.logout" />
            </span>
          </Dropdown.Item>
        </DropdownButton>
        <img src={profile ? profile : memberIcon} alt="" />
      </div>
    );
  }

  setMobileStyle() {
    if (this.state.isMobile) {
      const { theme, isPrimaryColorDarkContrast } = this.props;
      const textContrasted = isPrimaryColorDarkContrast ? '#FFF' : '#000';
      const { primary } = theme.colors || {};

      return (
        <style jsx global>
          {`
            .new-design-radio-input:checked + .new-design-radio-label {
              background-color: ${primary} !important;
              color: ${textContrasted} !important;
            }
          `}
        </style>
      );
    }
  }

  setStyle() {
    const { brand, theme, isPrimaryColorDarkContrast, isSecondaryColorDarkContrast } =
      this.props;

    const { branded } = brand || {};
    const { primary: backgroundColor, secondary: textColor } = theme.colors || {};
    const { primary: primaryBtn, secondary: secondaryBtn } = theme.buttons || {};
    const { bgcolor, radius } = theme.datetimePicker || {};
    const { hr } = theme || {};
    const datePickerBG = bgcolor || undefined;
    const pickerRadiusDefault = '40px';
    const headerHeightDefault = '60px';
    const colorPicker = '#D9D9D9';
    const textContrasted = isPrimaryColorDarkContrast ? '#FFF' : '#000';
    const textContrasted2 = isSecondaryColorDarkContrast ? textColor : '#000';
    const datePickerRad = String(radius) || undefined;
    const radiusValue = !!datePickerRad ? datePickerRad : pickerRadiusDefault;
    const bgcolorValue = !!datePickerBG ? datePickerBG : backgroundColor;
    const headerHeight = theme.headerHeight || headerHeightDefault;

    const {
      corporate: corpoIconUrl,
      private: privateIconUrl,
      vr: vrIconUrl
    } = branded.servicesIcons || {};

    const primaryBtnThemed = getCssThemeConfig(primaryBtn, true);
    const primaryBtnProps = getCssProps(primaryBtnThemed, backgroundColor);
    const secondaryBtnThemed = getCssThemeConfig(secondaryBtn, true);
    const secondaryBtnProps = getCssProps(secondaryBtnThemed, backgroundColor);
    const faqLastAccordion = '.faq .accordion-level2 .accordion-item .accordion-header';

    if (theme) {
      return (
        <style jsx global>
          {`
            :root {
              --height: ${headerHeight};
            }
            .primary {
              color: ${backgroundColor} !important;
            }

            .secondary {
              color: ${textContrasted2} !important;
            }

            .primary-border {
              border-color: ${backgroundColor} !important;
            }

            .primary-bg {
              ${primaryBtnProps};
              transition: all ease 0.3s;
            }
            .secondary-bg,
            .scope-submit-button.secondary-bg {
              ${secondaryBtnProps};
              transition: all ease 0.3s;
            }

            // apply-only-one-time classes -start-

            .primary-btn {
              ${primaryBtnProps}
            }

            .secondary-btn,
            .scope-submit-button.secondary-btn {
              ${secondaryBtnProps}
            }

            .switch-profile-page .scope-pseudo-btn {
              ${secondaryBtnProps}
            }

            // apply-only-one-time -end-

            .new-design-address-container input:focus,
            .new-design-field:focus {
              border-color: ${backgroundColor};
            }

            .nav-container #menu-mobile ul li .link.active {
              background-color: ${backgroundColor};
              color: ${textContrasted};
            }

            .border-primary-hover > button:hover {
              border-color: ${backgroundColor} !important;
            }

            .border-primary-active > button {
              border-color: ${backgroundColor} !important;
            }

            .subscription .offers .corporate {
              ${corpoIconUrl && `background-image: url('${corpoIconUrl}') !important;`};
            }
            .subscription .offers .private {
              ${privateIconUrl &&
              `background-image: url('${privateIconUrl}') !important;`};
            }
            .subscription .offers .vr {
              ${vrIconUrl && `background-image: url('${vrIconUrl}') !important;`};
            }

            #header {
              ${headerHeight !== '' ? 'height:' + headerHeight : ''}
            }

            .picker__day--infocus.picker__day--highlighted.picker__day--selected {
              background: ${bgcolorValue} !important;
              border-radius: ${radiusValue}px !important;
              color: white !important;
            }

            .picker__day--selected {
              background: ${bgcolorValue} !important;
              border-radius: ${radiusValue}px !important;
            }

            .picker__weekday {
              ${isPrimaryColorDarkContrast
                ? `color: ${bgcolorValue} !important;`
                : `color: #000 !important;`}
            }
            .picker__day--highlighted {
              border-color: ${bgcolorValue} !important;
            }

            .date-picker .picker__day--today.picker__day--highlighted {
              border-radius: ${pickerRadiusDefault} !important;
              color: #000 !important;
              border-color: #d9d9d9 !important;
              background: #d9d9d9 !important;
            }

            .picker__day--infocus:hover {
              border-radius: ${radiusValue} !important;
            }

            .date-picker .picker__day--disabled,
            .date-picker .picker__day--outfocus:hover {
              background: white !important;
              color: #f6f6f6 !important;
              border-color: white;
            }

            .date-picker .picker__day--outfocus:not(.picker__day--disabled):hover,
            .date-picker .picker__day--infocus:not(.picker__day--disabled):hover,
            .date-picker .picker__day--today.picker__day--highlighted:hover {
              background: #d9d9d9 !important;
              color: #000 !important;
              border-color: #d9d9d9 !important;
              border-radius: ${radiusValue} !important;
            }

            .picker__day--today.picker__day--highlighted.picker__day--selected {
              background: ${bgcolorValue} !important;
              border-radius: ${radiusValue}px !important;
              color: white !important;
            }

            .picker--focused .picker__list-item--selected,
            .picker--focused .picker__list-item--highlighted,
            .picker__list-item--selected,
            .picker__list-item--selected:hover,
            .picker__list-item--highlighted,
            .picker__list-item--highlighted:hover {
              background: ${bgcolorValue} !important;
              border-color: ${bgcolorValue} !important;
            }

            .filter-item .vehicle-type .radio-icon input:checked ~ label {
              background-color: ${backgroundColor} !important;
              color: ${textContrasted} !important;
            }

            input:checked + label .radio-btn,
            input:checked ~ label {
              border-color: ${backgroundColor} !important;
            }

            input:checked + label .radio-btn::after {
              background-color: ${backgroundColor} !important;
              color: ${textContrasted};
            }

            input[type='checkbox'] + label .chk-btn {
              border-color: ${backgroundColor} !important;
            }

            .FiltersMobile-popup input[type='checkbox']:checked + label .chk-btn:after,
            .FiltersMobile-popup input[type='checkbox']:checked + label .chk-btn,
            input[type='checkbox']:checked + label .chk-btn:after,
            input[type='checkbox']:checked + label .chk-btn {
              background-color: ${backgroundColor} !important;
              color: ${textContrasted};
            }

            .date-picker.picker--active,
            .time-picker.picker--active {
              border: 1px solid ${colorPicker} !important;
              border-radius: 4px;
            }

            .date-picker.picker--active .picker__holder {
              border: unset;
              margin-top: 5px;
              box-shadow: 0 0 3px rgba(0, 0, 0, 0.6) !important;
            }

            .subscribe .react-svg.back-arrow svg path,
            .scope-date-range .react-svg,
            .scope-content-header-button-return .react-svg {
              fill: ${backgroundColor};
            }

            .field-wrapper .input-wrapper .valid-icon,
            .field-wrapper .input-wrapper .valid-icon::before {
              color: ${backgroundColor};
            }

            .location-input .mapbox-complete .location-spinner {
              border-top-color: ${backgroundColor};
            }

            .search-form .mapbox-complete .mc-input:focus,
            .search-form .location input:focus,
            .search-form .location input[aria-expanded='true'] {
              border: 1px solid ${colorPicker} !important;
            }

            .search-form .mapbox-complete .mc-dropdown-menu {
              border: 1px solid ${colorPicker};
              box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
            }

            ${faqLastAccordion} button:hover,
            ${faqLastAccordion} button[aria-expanded='true'] {
              background: ${backgroundColor};
              color: ${textColor};
            }

            .booking-history .booking-history-tabs li button.active {
              border-color: ${backgroundColor};
              background: ${backgroundColor};
              color: ${textContrasted};
            }

            .booking-history-details-start-date-time {
              border-bottom: 2px solid ${backgroundColor};
            }

            .booking-history-details-content-footer {
              background-color: ${backgroundColor};
              color: ${textContrasted};
            }
            .booking-history-details-content.sub-class-mobile-msg
              .booking-history-details-content-footer
              .inprogress {
            }

            .new-design-radio-input:checked
              + .new-design-radio-label
              .new-design-radio-btn {
              border-color: ${backgroundColor} !important;
            }

            .new-design-radio-input:checked
              + .new-design-radio-label
              .new-design-radio-btn:after {
              background-color: ${backgroundColor} !important;
            }

            .profile-page .scope-menu-body-link-container a:hover {
              border-color: ${backgroundColor};
            }

            .booking-success-popup .react-add-to-calendar__dropdown li:hover,
            .booking-success-popup .react-add-to-calendar__dropdown li:hover a {
              background-color: ${backgroundColor};
              color: ${textContrasted};
            }

            .spinner {
              border-top-color: ${backgroundColor};
            }

            .register-page > .scope-page-header .scope-step.sub-class-active {
              background-color: ${backgroundColor};
              color: ${textContrasted};
            }

            .footer .container-language .dropdown.show .btn,
            .footer .container-language .dropup.show .btn {
              background-color: ${backgroundColor};
              color: ${textContrasted};
              fill: ${textContrasted};
            }

            .agency-circle svg circle,
            .search-filters-layout .search-detail div.svg-arrow {
              fill: ${backgroundColor};
            }

            .scope-credit-card-container {
              border: 2px solid ${backgroundColor};
            }

            .btn-book {
              color: ${textContrasted};
            }

            .agency-circle .figure {
              color: ${textContrasted};
            }

            .google-map-popup .agency-name {
              background-color: ${backgroundColor} !important;
              color: ${textContrasted} !important;
            }

            .bookings-map .map-info-wrap .arrow-down {
              border-color: ${backgroundColor} transparent transparent;
            }

            .ccpane .ccbackg,
            .ccb-copy,
            .ccb-close-label {
              background-color: ${backgroundColor} !important;
              color: ${textColor} !important;
            }

            .scope-button-cancel {
              background-color: ${backgroundColor};
              color: ${textContrasted};
            }

            .business-tabs div:first-child::before {
              background-color: ${textColor};
            }

            .car-popup .wrapper li:before,
            .car-popup .wrapper li:after {
              background-color: ${textColor};
            }

            .scope-booking-recap-hr,
            .hr-dashed,
            .login hr {
              margin: 20px 0;
              ${getCssThemeConfig(hr)}
            }
          `}
        </style>
      );
    }
  }

  searchBookingsHomeForm() {
    const {
      user,
      brand: { branded },
      user: { companyIdError },
      searchValues
    } = this.props;

    const isRestrictedAccess = branded.restrictedAccess;

    if ((!isRestrictedAccess || user.data) && isBookingPage()) {
      return (
        <div id="search-form-result">
          <SearchBookingsHomepageForm
            onSubmit={this.searchBookings}
            i18n={this.props.i18n}
            contract={branded.contract}
            user={user.data}
            initialValues={searchValues}
            updateOnChange
            requestError={companyIdError}
            classname="search-header"
            noExpandingClick={false}
          />
        </div>
      );
    }
  }

  // A registration with nothing created yet, on one of its own screens: a Keycloak
  // identity we still have no member for. That is exactly as far as Cancel means
  // anything - the moment the first step registers him there is nothing left to
  // cancel, the rest of the flow is him filling in a profile he already owns, and the
  // way out of it is the ordinary one.
  isKeycloakCreation() {
    return isRegistrationFlowPage() && this.props.isExternalIncompleteUser;
  }

  renderCancelRegistration() {
    if (this.isKeycloakCreation()) {
      return (
        <li>
          <button
            type="button"
            className="login btn secondary-btn"
            onClick={this.openRegistrationCancelPopup}
          >
            <FormattedMessage id="generic.cancel" />
          </button>
        </li>
      );
    }
  }

  userHeader() {
    const {
      brand: {
        branded: {
          contract: {
            interfaceConfig: { replacementVehicleDisplay: isRV }
          },
          mapCountries
        }
      },
      userCompanyId
    } = this.props;

    return (
      <ul id="links" className="set-height">
        {this.renderCancelRegistration()}
        {!isRV && (
          <li>
            <IndexLink to="/book" activeClassName="active">
              <FormattedMessage id="generic.book" />
            </IndexLink>
          </li>
        )}
        <li>
          <IndexLink to="/my-bookings" activeClassName="active">
            <FormattedMessage id="label.my_reservations" />
          </IndexLink>
        </li>
        <li>
          {getFaqLink({
            label: <FormattedMessage id="menu.faq" />,
            linkProps: { activeClassName: 'active' },
            userCompanyId,
            mapCountries,
            action: this.openFaqPopup
          })}
        </li>
        <li className="dropdown-li">{this.renderDropdownButton()}</li>
        <li className="tablet-profile-link">
          <IndexLink to="/profile/personal-info" activeClassName="active">
            <FormattedMessage id="label.my_account" />
          </IndexLink>
        </li>
        <li className="tablet-profile-link">
          <button
            type="button"
            className="logout"
            onClick={this.props.handleKeycloakLogout}
          >
            <FormattedMessage id="label.logout" />
          </button>
        </li>
      </ul>
    );
  }

  makeBookingUrl() {
    const { brand } = this.props;
    const { branded } = brand;
    const { restrictedAccess } = branded || {};

    if (!restrictedAccess) {
      return (
        <li>
          <IndexLink to="/book" activeClassName="active" onClick={this.closeMobileMenu}>
            <FormattedMessage id="generic.book" />
          </IndexLink>
        </li>
      );
    }
  }

  noUserHeader() {
    const { isTablet } = this.state;

    const {
      brand: {
        branded: { mapCountries }
      },
      userCompanyId
    } = this.props;

    const clname =
      this.isHeaderBurger() && isTablet ? 'login' : 'login btn secondary-btn';

    return (
      <ul id="links">
        {this.renderCancelRegistration()}
        {this.makeBookingUrl()}
        <li>
          {getFaqLink({
            label: <FormattedMessage id="menu.faq" />,
            linkProps: { activeClassName: 'active' },
            userCompanyId,
            mapCountries,
            action: this.openFaqPopup
          })}
        </li>
        <li>
          <span className={clname} onClick={this.handleLoginClick}>
            <FormattedMessage id="label.login" />
          </span>
        </li>
      </ul>
    );
  }

  mobileHeader() {
    const burgerIconClasses = classnames('burger-icon', {
      open: this.state.mobileMenu
    });

    if (!this.isSearchBookingsLayout())
      return (
        <header id="header-mobile">
          <div className="container pos-rel h-100 text-center">
            <div className={burgerIconClasses} onClick={this.toggleMobileMenu}>
              <span />
              <span />
              <span />
            </div>
            <IndexLink to="/">{this.renderLogoBgw()}</IndexLink>
            {this.isKeycloakCreation() && (
              <button
                type="button"
                className="close mobile-cancel-registration"
                onClick={this.openRegistrationCancelPopup}
              >
                <i className="icon-cross" />
              </button>
            )}
          </div>
        </header>
      );
  }

  getHeaderBanner() {
    const { brand } = this.props;
    const { branded } = brand || {};

    if (branded.preHeaderRessource && isHomePage()) {
      return (
        <div className="banner-header">
          <span
            dangerouslySetInnerHTML={{
              __html: branded.preHeaderRessource[this.getIntlLocale()]
            }}
          />
        </div>
      );
    }
  }

  getHeader() {
    const { user } = this.props;
    const homePage = isHomePage();

    const homePageClass = classnames({
      landing: homePage,
      'booking-page': this.isHeaderBurger()
    });

    const headerClassname = classnames('hidden-xs upper-section-wrapper', {
      landing: homePage
    });

    if (!this.isSearchBookingsLayout())
      return (
        <div className={headerClassname}>
          <header id="header">
            <nav id="user-menu" className={homePageClass}>
              <IndexLink to="/">
                {homePage ? this.renderLogo() : this.renderLogoBgw()}
              </IndexLink>
              {this.searchBookingsHomeForm()}
              <input className="menu-btn" type="checkbox" id="menu-btn" />
              <label className="menu-icon" htmlFor="menu-btn">
                <span className="navicon" />
              </label>
              {user.data ? this.userHeader() : this.noUserHeader()}
            </nav>
          </header>
        </div>
      );
  }

  getFooter() {
    if (this.canShowFooter() && !this.isSearchBookingsLayout()) return <Footer />;
  }

  mobileMakeBookingUrl() {
    const { brand } = this.props;
    const { branded } = brand;
    const { restrictedAccess } = branded || {};

    if (!restrictedAccess) {
      return (
        <li>
          <IndexLink
            to="/book"
            className="link"
            activeClassName="active"
            onClick={this.closeMobileMenu}
          >
            <FormattedMessage id="generic.book" />
          </IndexLink>
        </li>
      );
    }
  }

  mobileNoUserMenu() {
    const {
      brand: {
        branded: { mapCountries }
      },
      userCompanyId
    } = this.props;

    return (
      <ul className="w-100">
        <li className="logo">
          <IndexLink to="/" onClick={this.closeMobileMenu}>
            {this.renderLogoBgw()}
          </IndexLink>
        </li>
        {this.mobileMakeBookingUrl()}
        <li>
          {getFaqLink({
            label: <FormattedMessage id="menu.faq" />,
            linkProps: {
              activeClassName: 'active',
              className: 'link',
              onClick: this.closeMobileMenu
            },
            userCompanyId,
            mapCountries,
            action: this.openFaqPopup
          })}
        </li>
        <li onClick={this.closeMobileMenu}>
          <span className="link" onClick={this.handleLoginClick}>
            <FormattedMessage id="label.login" />
          </span>
        </li>
      </ul>
    );
  }

  getMobileUserStatus() {
    const { i18n } = this.props;
    const { messages } = i18n;
    const userData = this.props.user.data;
    const { status, suspended } = userData || {};
    const userStatus = suspended ? 'profile.suspended' : status;

    return (
      <div className={classnames('status', status, { suspended: suspended })}>
        {getMessageOptions(messages, keysMemberStatus, userStatus)}
      </div>
    );
  }

  mobileUserMenu() {
    const {
      user,
      brand: {
        branded: {
          contract: {
            interfaceConfig: { replacementVehicleDisplay: isRV }
          },
          mapCountries
        }
      },
      theme,
      userCompanyId
    } = this.props;
    const { profile } = theme.icons || {};

    return (
      <ul className="w-100">
        <li>
          <span className="member-known">
            <img src={profile ? profile : memberIcon} className="member-pic" alt="" />
            {user.data.firstName} {user.data.lastName}
            {this.getMobileUserStatus()}
          </span>
        </li>
        {!isRV && (
          <li>
            <IndexLink
              to="/book"
              className="link"
              activeClassName="active"
              onClick={this.closeMobileMenu}
            >
              <FormattedMessage id="generic.book" />
            </IndexLink>
          </li>
        )}
        <li>
          <IndexLink
            to="/my-bookings"
            className="link"
            activeClassName="active"
            onClick={this.closeMobileMenu}
          >
            <FormattedMessage id="label.my_reservations" />
          </IndexLink>
        </li>
        <li>
          <IndexLink
            className="link"
            to="/profile"
            activeClassName="active"
            onClick={this.closeMobileMenu}
          >
            <FormattedMessage id="label.my_account" />
          </IndexLink>
        </li>
        <li>
          {getFaqLink({
            label: <FormattedMessage id="menu.faq" />,
            linkProps: {
              activeClassName: 'active',
              className: 'link',
              onClick: this.closeMobileMenu
            },
            userCompanyId,
            mapCountries,
            action: this.openFaqPopup
          })}
        </li>
        <li onClick={this.closeMobileMenu}>
          <span className="link logout" onClick={_partial(this.props.handleKeycloakLogout)}>
            <FormattedMessage id="label.logout" />
          </span>
        </li>
      </ul>
    );
  }

  getMobileMenu() {
    const { user } = this.props;

    const mobileNavClasses = classnames('nav-container', {
      'mobile-nav-opened': this.state.mobileMenu
    });

    return (
      <div className="visible-xs">
        <div className={mobileNavClasses}>
          <div className="overlay" onClick={this.toggleMobileMenu} />
          <nav id="menu-mobile">
            {user.data ? this.mobileUserMenu() : this.mobileNoUserMenu()}
          </nav>
        </div>
        {this.mobileHeader()}
      </div>
    );
  }

  faqPopup() {
    if (this.props.popup.type_Faq_IsVisible) {
      return (
        <Popup
          isOpen={this.props.popup.type_Faq_IsVisible}
          onCloseRequested={this.closeFaqPopup}
          id="faq-popup-modal"
        >
          <FaqPopup />
        </Popup>
      );
    }
  }

  mobileAppSelectorPopup() {
    if (this.props.popup.type_MobileAppSelector_IsVisible) {
      return (
        <Popup
          isOpen={this.props.popup.type_MobileAppSelector_IsVisible}
          onCloseRequested={this.closeMobileAppSelectorPopup}
          id="mobile-app-selector-popup-modal"
        >
          <MobileAppSelectorPopup appType={this.props.popup.mobileAppSelectorType} />
        </Popup>
      );
    }
  }

  vrInfoPopup() {
    if (this.props.popup.type_VrInfo_IsVisible) {
      return (
        <Popup
          isOpen={this.props.popup.type_VrInfo_IsVisible}
          onCloseRequested={this.closeVrInfoPopup}
          id="vr-info-popup"
        >
          <VrInfoPopup />
        </Popup>
      );
    }
  }

  bookingEditPopup() {
    if (this.props.popup.type_BookingEdit_IsVisible) {
      return (
        <Popup
          isOpen
          onCloseRequested={this.closeBookingEditPopup}
          buttonPositionStatic
          id="bookingEditPopup"
        >
          <BookingEditPopup />
        </Popup>
      );
    }
  }

  creditCardPopup() {
    if (this.props.popup.type_CreditCard_IsVisible) {
      return (
        <Popup
          isOpen={this.props.popup.type_CreditCard_IsVisible}
          onCloseRequested={_partial(this.props.popup_CreditCard_SetVisibility, false)}
          id="creditCard-popup"
        >
          <CreditCardPopup
            handleSubmit={this.handleGetCreditCard}
            handleCancel={this.closeCreditCardPopup}
          />
        </Popup>
      );
    }
  }

  confirmAnonymPopup() {
    if (this.props.popup.type_AnonymConfirm_IsVisible) {
      return (
        <Popup
          isOpen={this.props.popup.type_AnonymConfirm_IsVisible}
          onCloseRequested={this.closeAnonymizeConfirmPopup}
          id="anonymConfirm-popup"
        >
          <AnonymizeConfirmPopup handleSubmit={this.handleAnonymizeConfirmPopup}>
            <FormattedMessage id="anonymize.confirm.description" />
          </AnonymizeConfirmPopup>
        </Popup>
      );
    }
  }

  registrationCancelPopup() {
    if (this.props.popup.type_RegistrationCancel_IsVisible) {
      return (
        <Popup
          isOpen={this.props.popup.type_RegistrationCancel_IsVisible}
          onCloseRequested={this.closeRegistrationCancelPopup}
          id="registrationCancel-popup"
        >
          <ConfirmPopup
            handleSubmit={this.handleConfirmCancelRegistration}
            submitMsg={<FormattedMessage id="generic.yes" />}
            cancelMsg={<FormattedMessage id="generic.no" />}
          >
            <FormattedMessage id="label.cancel_registration_confirm" />
          </ConfirmPopup>
        </Popup>
      );
    }
  }

  paymentErrorPopup() {
    return <PaymentErrorPopup />;
  }

  genericErrorPopup() {
    if (this.props.popup.type_Generic_IsVisible) {
      return (
        <Popup
          isError
          isOpen={this.props.popup.type_Generic_IsVisible}
          onCloseRequested={this.hideGenericPopupAction}
          id="generic-error-popup"
        >
          <GenericPopup />
        </Popup>
      );
    }
  }

  activationPopup() {
    if (this.props.popup.type_AccountValidation_IsVisible) {
      return (
        <Popup
          isOpen={this.props.popup.type_AccountValidation_IsVisible}
          onCloseRequested={_partial(
            this.props.popup_AccountValidation_SetVisibility,
            false
          )}
          id="account-validation-confirmation-popup"
        >
          {this.renderActivationPopup()}
        </Popup>
      );
    }
  }

  activationEmailSentPopup() {
    if (this.props.popup.type_ActivationEmailSent_IsVisible) {
      return (
        <Popup
          isOpen={this.props.popup.type_ActivationEmailSent_IsVisible}
          onCloseRequested={_partial(
            this.props.popup_ActivationEmailSent_SetVisibility,
            false
          )}
          id="activation-email-sent-popup-css"
        >
          {this.renderActivationEmailSentPopup()}
        </Popup>
      );
    }
  }

  recapPopup() {
    if (this.props.popup.type_BookingRecap_IsVisible) {
      return (
        <Popup
          isOpen={this.props.popup.type_BookingRecap_IsVisible}
          onCloseRequested={this.closeRecapPopup}
          dark={true}
          id="recap-popup"
          outerClose={true}
          backdropClass="recap-booking"
        >
          <BookingRecapPopup />
        </Popup>
      );
    }
  }

  bookingCancelPopup() {
    if (this.props.popup.type_BookingCancel_IsVisible) {
      return (
        <Popup
          isOpen={this.props.popup.type_BookingCancel_IsVisible}
          onCloseRequested={_partial(this.closeBookingCancelPopup)}
          id="booking-cancel-popup"
        >
          <BookingCancelPopup />
        </Popup>
      );
    }
  }

  bookingSuccessPopup() {
    if (this.props.popup.type_BookingSuccess_IsVisible) {
      return (
        <Popup
          isOpen={this.props.popup.type_BookingSuccess_IsVisible}
          onCloseRequested={this.closeBookingSuccessPopup}
          id="booking-success-popup"
        >
          <BookingSuccessPopup />
        </Popup>
      );
    }
  }

  renderPopups() {
    return (
      <div>
        {this.bookingEditPopup()}
        {this.faqPopup()}
        {this.mobileAppSelectorPopup()}
        {this.vrInfoPopup()}
        {this.creditCardPopup()}
        {this.paymentErrorPopup()}
        {this.genericErrorPopup()}
        {this.activationPopup()}
        {this.activationEmailSentPopup()}
        {this.recapPopup()}
        {this.bookingCancelPopup()}
        {this.bookingSuccessPopup()}
        {this.confirmAnonymPopup()}
        {this.registrationCancelPopup()}
      </div>
    );
  }

  getHowItWorks() {
    if (isHomePage()) return <HowItWorks />;
  }

  getIntlLocale() {
    const { locale } = this.props.i18n;

    return pt_BR_map[locale] || locale;
  }

  // The brand config is the whole site - theme, API base URL, Keycloak realm - so a load
  // failure has no degraded mode to fall back to and this screen is terminal. It is
  // plain on purpose: the theme that would style it is exactly what failed to arrive,
  // and it renders outside IntlProvider, which sits inside the app tree it replaces.
  renderConfigError() {
    return (
      <div className="app-boot-error">
        <p>{t(this.props.i18n.messages, 'error.get.site.config')}</p>
      </div>
    );
  }

  displayMainApp() {
    return (
      <IntlProvider
        key={this.props.i18n.locale}
        messages={this.props.i18n.messages}
        locale={this.getIntlLocale()}
      >
        <IntlGlobalProvider>
          <div className={classnames('site-container', this.getPageClass())}>
            {this.renderPopups()}
            {this.getHeaderBanner()}

            <div className="main-background" style={this.renderBackgroundProps()}>
              {this.getMobileMenu()}
              {this.getHeader()}
              <main>
                <Outlet />
              </main>
            </div>

            {this.getHowItWorks()}
            {this.getFooter()}
            {this.setStyle()}
            {this.setMobileStyle()}
          </div>
        </IntlGlobalProvider>
      </IntlProvider>
    );
  }

  render() {
    const { i18n, brand, showAppLoader } = this.props;
    const { configFetched, configError } = brand;
    const { messages } = i18n;

    kcDebugRenderCalls += 1;

    // Deduped on purpose: React re-renders often and an unfiltered log here would
    // bury the Keycloak timeline. Only actual gate transitions are printed.
    const kcDebugRenderSignature = [
      Boolean(showAppLoader),
      Boolean(configFetched),
      Boolean(configError),
      Boolean(messages),
      Boolean(this.props.userId)
    ].join('|');

    if (kcDebugRenderSignature !== kcDebugLastRenderSignature) {
      kcDebugLastRenderSignature = kcDebugRenderSignature;
      console.log('[KC-DEBUG] App.js/render: RENDER GATE CHANGED - what is actually on screen now', { renderCall: kcDebugRenderCalls, showsSpinner: Boolean(showAppLoader), showsApp: Boolean(messages && configFetched), showsConfigError: Boolean(configError), showAppLoader: Boolean(showAppLoader), configFetched: Boolean(configFetched), configError: Boolean(configError), hasMessages: Boolean(messages), userId: this.props.userId, isLoggedIn: Boolean(this.props.userId), signature: kcDebugRenderSignature, atIso: new Date().toISOString(), perfNowMs: Math.round(performance.now()) }); // eslint-disable-line no-console
    }

    return (
      <div className={classnames('root-container', { 'app-loading': showAppLoader })}>
        {showAppLoader && <AppSpinner />}
        {configError && this.renderConfigError()}
        {messages && configFetched && this.displayMainApp()}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    ...state,
    m: messagesSelector(state),
    showAppLoader: showAppLoaderSelector(state),
    userCompanyId: userCompanyIdSelector(state),
    searchValues: searchValuesSelector(state),
    keycloakConfig: keycloakConfigSelector(state),
    registrationFlags: keycloakRegistrationFlagsSelector(state),
    brandId: brandIdSelector(state),
    profileCompanyIds: profileCompanyIdsSelector(state),
    isAppliedUser: isAppliedUserSelector(state),
    isIncompleteUser: isIncompleteUserSelector(state),
    isExternalIncompleteUser: isExternalIncompleteUserSelector(state),
    userId: userIdSelector(state),
    theme: currentThemeSelector(state),
    isPrimaryColorDarkContrast: isPrimaryColorDarkSelector(state),
    isSecondaryColorDarkContrast: isSecondaryColorDarkSelector(state)
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      ...BookingActions,
      ...CreditCardActions,
      ...UserActions,
      ...PopupActions,
      ...i18nActions,
      ...BrandActions,
      ...KeycloakActions,
      ...ImpersonateActions,
      startLoginOrExplain
    },
    dispatch
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(App);
